import React, { useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { FixedSizeList as List } from 'react-window';

import optimizeImage from '../../../shared/utils/imageOptimizer';
import Text from '../../../shared/components/Text/Text';

const LIST_HEIGHT = 300;
const ROW_HEIGHT = 55;

// Add a styled wrapper for the List component with custom scrollbar styles
const StyledList = styled(List)`
  /* Hide the default scrollbar track */
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  /* Style the scrollbar thumb */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  /* Style the scrollbar thumb */
  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.colors.scrollbarThumb};
    border-radius: 3px;
  }
  
  /* For Firefox */
  scrollbar-width: thin;
  scrollbar-color: ${({ theme }) => theme.colors.scrollbarThumb} transparent;
`;

const AthleteContainer = styled('div')`
  display: flex;
  padding: 10px;
  cursor: ${({ isFake }) => (isFake ? 'default' : 'pointer')};
  background-color: ${({ isSelected, theme }) => (isSelected ? theme.colors.surfaceSecondary : 'transparent')};
  &:hover {
    background-color: ${({ isFake, theme }) => (isFake ? 'transparent' : theme.colors.surfaceSecondary)};
  }
  img {
    height: 30px;
    width: 30px;
  }
  h3 {
    color: ${({ theme }) => theme.colors.textStrong};
  }
  svg {
    visibility: hidden;
  }
  @media screen and (orientation: portrait) {
    padding: 10px 20px!important;
  }
`;

const ListAthleteAvatar = styled('div')`
  img {
    height: ${(props) => (
    props.size ? `${props.size}px` : '25px')};
    width: ${(props) => (
    props.size ? `${props.size}px` : '25px')};
    border-radius: 50%;
    margin-right: 10px;
  }
  display: flex;
  align-items: center;
`;

const AthleteNameContainer = styled('div')`
  display: flex;
  align-items: center;
`;

const ListAthleteName = styled('h3')`
  font-family: 'Nunito Sans';
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 0px;
  align-items: center;
  font-weight: 600;
  font-size: 18px;
  @media screen and (orientation: portrait) {
    font-size: 13px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: ${(props) => (props.small ? '85px;' : 'none')}
  }
`;

/**
 * @param {Object} data an object that's passed in from the 'itemData' prop in the list component
 * seen below
 * @param {Integer} index the index of the current data object in the passed array
 * @param {Object} style necessary object that styles each individual container in accordance with
 * the requirements of the list
 * @returns a formatted list of dynamically rendered athlete rows
 */
const AthleteRenderer = ({
  data, index, style,
}) => {
  const {
    list, onOptionClicked, selectedAthleteId,
  } = data;
  const athlete = list[index];
  const isFakeAthlete = athlete.fake;
  const isSelected = !isFakeAthlete && athlete.id === selectedAthleteId;
  const image = optimizeImage(
    athlete.pic,
    {
      resize: {
        width: 70,
        height: 70,
        fit: 'cover',
      },
    },
  );

  return (
    <AthleteContainer
      onClick={isFakeAthlete ? undefined : () => {
        onOptionClicked(athlete);
      }}
      id={athlete.userId}
      isFake={isFakeAthlete}
      isSelected={isSelected}
      aria-current={isSelected || undefined}
      style={style}
    >
      {athlete.pic ? (
        <ListAthleteAvatar>
          <img src={image} alt='' />
        </ListAthleteAvatar>
      ) : null}
      <AthleteNameContainer>
        <ListAthleteName>
          <Text>
            {`${athlete.first} ${athlete.last}`}
          </Text>
        </ListAthleteName>
      </AthleteNameContainer>
    </AthleteContainer>
  );
};

/**
 *
 * @param {Array} list the list of athletes to be passed into a generated windowed list
 * @param {Function} reduxFunction the function to be called when the user clicks on
 * an athlete row in the windowed list
 * @param {Object} visibleContainerItem the item that is generated in the selector container
 * i.e. the currently selected item
 * @returns
 */
const CalendarSelectAthleteList = ({
  reduxFunction,
  calendarSelectAthletes,
  isUserSearching,
  searchedAthleteList,
  selectedAthleteId,
}) => {
  const listOuterRef = useRef(null);

  const selectedIndex = calendarSelectAthletes.findIndex(
    (athlete) => athlete.id === selectedAthleteId,
  );

  // Effect to scroll to selected athlete - matches the Calendars tab: wait for
  // the modal slide-in, then smooth-scroll the athlete to the center of the list.
  // The list is virtualized, so the row may not be in the DOM to scrollIntoView;
  // scroll the list's own container to the row's offset instead.
  useEffect(() => {
    if (selectedIndex < 0) return undefined;
    const scrollOffset = Math.max(0, selectedIndex * ROW_HEIGHT - (LIST_HEIGHT - ROW_HEIGHT) / 2);
    const timeoutId = setTimeout(() => {
      listOuterRef.current?.scrollTo({ top: scrollOffset, behavior: 'smooth' });
    }, 350);
    return () => clearTimeout(timeoutId);
  }, [selectedIndex]);

  const onOptionClicked = (clickedOption) => {
    reduxFunction(clickedOption);
  };

  const listMaker = () => {
    if (isUserSearching) {
      if (searchedAthleteList.length) {
        return searchedAthleteList;
      }
    }
    return calendarSelectAthletes;
  };

  return (
    <StyledList
      height={LIST_HEIGHT}
      outerRef={listOuterRef}
      itemData={{
        list: listMaker(),
        onOptionClicked,
        isUserSearching,
        selectedAthleteId,
      }}
      itemCount={!isUserSearching ? calendarSelectAthletes.length : searchedAthleteList.length}
      itemSize={ROW_HEIGHT}
      width='100%'
      className='fixed-list'
    >
      {AthleteRenderer}
    </StyledList>
  );
};

AthleteRenderer.propTypes = {
  data: PropTypes.instanceOf(Object).isRequired,
  index: PropTypes.number.isRequired,
  style: PropTypes.instanceOf(Object).isRequired,
};

CalendarSelectAthleteList.propTypes = {
  reduxFunction: PropTypes.func.isRequired,
  calendarSelectAthletes: PropTypes.instanceOf(Array).isRequired,
  isUserSearching: PropTypes.bool.isRequired,
  searchedAthleteList: PropTypes.instanceOf(Array).isRequired,
  selectedAthleteId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

CalendarSelectAthleteList.defaultProps = {
  selectedAthleteId: null,
};

export default CalendarSelectAthleteList;
